/**
 * HLS interface
 */
'use strict';

import Event from './events';
import {ErrorTypes, ErrorDetails} from './errors';
import PlaylistLoader from './loader/playlist-loader';
import FragmentLoader from './loader/fragment-loader';
import KeyLoader from './loader/key-loader';

import StreamController from  './controller/stream-controller';
import LevelController from  './controller/level-controller';
import StreamCoordinator from './controller/stream-coordinator'; //Mactrix line added, add stream coordinator

import LicenseManager from '../drm/LicenseManager';

import {logger, enableLogs} from './utils/logger';
import EventEmitter from 'events';
import {hlsDefaultConfig} from './config';

//Mactrix additions start
import Utils from '../HlsUtils';
import BufferHelper from './helper/buffer-helper';
//Mactrix additions end

class Hls {

	static get version() {
		// replaced with browserify-versionify transform
		return '__VERSION__';
	}

	static isSupported() {
		window.MediaSource = window.MediaSource || window.WebKitMediaSource;
		return (window.MediaSource &&
			typeof window.MediaSource.isTypeSupported === 'function' &&
			window.MediaSource.isTypeSupported('video/mp4; codecs="avc1.42E01E,mp4a.40.2"'));
	}

	static get Events() {
		return Event;
	}

	static get ErrorTypes() {
		return ErrorTypes;
	}

	static get ErrorDetails() {
		return ErrorDetails;
	}

	static get DefaultConfig() {
		if(!Hls.defaultConfig) {
		return hlsDefaultConfig;
		}
		return Hls.defaultConfig;
	}

	static set DefaultConfig(defaultConfig) {
		Hls.defaultConfig = defaultConfig;
	}


	constructor(config = {}) {
		var defaultConfig = Hls.DefaultConfig;

		if ((config.liveSyncDurationCount || config.liveMaxLatencyDurationCount) && (config.liveSyncDuration || config.liveMaxLatencyDuration)) {
		throw new Error('Illegal hls.js config: don\'t mix up liveSyncDurationCount/liveMaxLatencyDurationCount and liveSyncDuration/liveMaxLatencyDuration');
		}

		for (var prop in defaultConfig) {
			if (prop in config) { continue; }
			config[prop] = defaultConfig[prop];
		}

		if (config.liveMaxLatencyDurationCount !== undefined && config.liveMaxLatencyDurationCount <= config.liveSyncDurationCount) {
		throw new Error('Illegal hls.js config: "liveMaxLatencyDurationCount" must be gt "liveSyncDurationCount"');
		}

		if (config.liveMaxLatencyDuration !== undefined && (config.liveMaxLatencyDuration <= config.liveSyncDuration || config.liveSyncDuration === undefined)) {
		throw new Error('Illegal hls.js config: "liveMaxLatencyDuration" must be gt "liveSyncDuration"');
		}

		enableLogs(config.debug);
		this.config = config;
		this._autoLevelCapping = -1;
		this._minAutoBitrate = this.config.minAutoBitrate; //Mactrix line added, exposing variable so we can change it
		// observer setup
		var observer = this.observer = new EventEmitter();
		observer.trigger = function trigger (event, ...data) {
			observer.emit(event, event, ...data);
		};

		observer.off = function off (event, ...data) {
			observer.removeListener(event, ...data);
		};
		this.on = observer.on.bind(observer);
		this.off = observer.off.bind(observer);
		this.trigger = observer.trigger.bind(observer);

		this.browChrome = Utils.isChromium();
		this.browEdge = Utils.isEdge();
		this.browIE = Utils.isIE();
		this.browIE81 = Utils.isIEWin8Point1();
		this.browFirefox = Utils.isFirefox();
		this.browSafari = Utils.isSafari();
		this.browTizen2 = Utils.isTizen2();
		this.browTizen3 = Utils.isTizen3();

		this._useSlowDetach = false;
		this._inSlowDetach = false;
		if (this.browSafari) {
			this._useSlowDetach = true;
		}
		this._preBufferSeeks = false;
		if (this.browIE81) {
			this._preBufferSeeks = true;
		}

		// core controllers and network loaders
		const abrController = this.abrController = new config.abrController(this);
		const bufferController = this.bufferController = new config.bufferController(this);
		const capLevelController = new config.capLevelController(this);
		const fpsController = new config.fpsController(this);
		const playListLoader = new PlaylistLoader(this);
		const fragmentLoader = new FragmentLoader(this);
		const keyLoader = this.keyLoader = new KeyLoader(this);

		// network controllers
		const levelController = this.levelController = new LevelController(this);
		const streamController = this.streamController = new StreamController(this);
		let networkControllers = [levelController, streamController];

		const streamCoordinator = this.streamCoordinator = new StreamCoordinator(this);

		if(config.obtainKeySystemConfigCallback) {
			const licenseManager = this.licenseManager = new LicenseManager(this, config.obtainKeySystemConfigCallback);
		}

		// optional audio stream controller
		let Controller = config.audioStreamController;
		if (Controller) {
			this.audioStreamController = new Controller(this);
			networkControllers.push(this.audioStreamController);
		}
		this.networkControllers = networkControllers;

		let coreComponents = [ playListLoader, fragmentLoader,/* keyLoader,*/ abrController, bufferController, capLevelController, fpsController ];

		// optional controllers
		Controller = config.audioTrackController;
		if (Controller) {
			let audioTrackController = new Controller(this);
			this.audioTrackController = audioTrackController;
			coreComponents.push(audioTrackController);
		}
		Controller = config.subtitleTrackController;
		if (Controller) {
			let subtitleTrackController = new Controller(this);
			this.subtitleTrackController = subtitleTrackController;
			coreComponents.push(subtitleTrackController);
		}
		Controller = config.subtitleStreamController;
		if (Controller) {
			let subtitleStreamController = new Controller(this);
			coreComponents.push(subtitleStreamController);
		}
		Controller = config.timelineController;
		if (Controller) {
			let timelineController = new Controller(this);
			this.timelineController = timelineController;
			coreComponents.push(timelineController);
		}

		this.coreComponents = coreComponents;
	}

	destroy() {
		logger.log('hlsjs destroy');
		this.trigger(Event.DESTROYING);
		this.detachMedia();
		this.coreComponents.push(this.keyLoader);// mactrix adddition: add keyLoader for destruction
		this.coreComponents.concat(this.networkControllers).forEach(component => {component.destroy();});
		this.streamCoordinator.dispose(); //Mactrix line added, destroy stream coordinator
		this.url = null;
		this.observer.removeAllListeners();
		this._autoLevelCapping = -1;
	}

	attachMedia(media) {
		logger.log('hlsjs attachMedia');
		this.media = media instanceof HTMLMediaElement ? media : null;

		// if we have a valid Media Element call attaching, otherwise we're detached.
		this.trigger( this.media ? Event.MEDIA_ATTACHING : Event.MEDIA_DETACHED, { media: this.media });
	}


	detachMedia() {
		logger.log('hlsjs detachMedia');
	// Mactrix addition start
	if (!this._useSlowDetach || !this.media || this.media.buffered.length === 0 ||
		(this.media.buffered.end(this.media.buffered.length - 1) + 1.0 < this.media.currentTime)) {
	// Mactrix addition end
		this.trigger(Event.MEDIA_DETACHING);
		this.media = null;
	// Mactrix addition start
		}
		else if (!this._inSlowDetach) {
			logger.log('slow detach start');
			this._inSlowDetach = true;
			this.stopFetch();
			this.stopLoad();
			this.media.currentTime = this.media.buffered.end(this.media.buffered.length - 1) - 0.5;
		}
	// Mactrix addition end
	}


	loadSource(url) {
		//logger.log(`loadSource:${url}`);
		this.url = url;
		// when attaching to a source URL, trigger a playlist load
		this.trigger(Event.MANIFEST_LOADING, {url: url});
	}

	/* mactrix replace
	startLoad(startPosition=-1) {
		logger.log(`startLoad(${startPosition})`);
		this.networkControllers.forEach(controller => {controller.startLoad(startPosition);});
	}
	*/
	// mactrix additions begin
	// we need to pass in liveStreamsStartAtLiveTime into lower levels to enable our enhanced liveStream startPosition functionality.
	startLoad(startPosition=-1, liveStreamsStartAtLiveTime=false) {
		logger.log(`startLoad(${startPosition}, LSSALT: ${liveStreamsStartAtLiveTime})`);
		//pass start time information to stream coordinator
		this.streamCoordinator.startTime = startPosition;
		this.streamCoordinator.liveStreamsStartAtLiveTime = liveStreamsStartAtLiveTime;
		this.networkControllers.forEach(controller => {controller.startLoad();});
	}
	// mactrix additions end

	stopLoad() {
		logger.log('stopLoad');
		this.networkControllers.forEach(controller => {controller.stopLoad();});
		//Mactrix additions start
		//stop audio track reloading on stopLoad
		if(this.audioTrackController) {
			this.audioTrackController.stopLoad();
		}
		//Mactrix additions end
	}


	//Mactrix additions begin
	//We want to be able to stop/start fetching chunks without stopping fetching variants
	stopFetch() {
		logger.log('stopFetch');
		this.streamController.forceStopFetching = true;
		if (this.audioStreamController){
			this.audioStreamController.forceStopFetching = true;
		}
	}


	resumeFetch(resumePosition = -1) {
		logger.log('resumeFetch');
		this.streamCoordinator.resetStreamTimeout();
		this.streamController.forceStopFetching = false;
		if (this.audioStreamController){
			this.audioStreamController.forceStopFetching = false;
		}
	}
	//Mactrix additions end

	swapAudioCodec() {
		logger.log('swapAudioCodec');
		this.streamController.swapAudioCodec();
	}

	recoverMediaError() {
		logger.log('recoverMediaError');
		var media = this.media;
		this.detachMedia();
		this.attachMedia(media);
	}

	//Mactrix additions start
	// FIXME: will need to be checked.
	flushBuffers(levelFlush) {
		if(this.media) {
			if(this.levelController.chunkIDAtTime(this.media.currentTime) !== -1) {
				//Win8.1 IE needs more room after a flush or it will try to reload the current frag and cause a lot of graphical glitches
				if(this.browIE81) {
					if(BufferHelper.isBuffered(this.media, (this.levelController.endOfChunk(this.levelController.chunkIDAtTime(this.media.currentTime) + 3) - 1))) {
						this.trigger(Event.BUFFER_FLUSHING, {
							startOffset: this.levelController.endOfChunk(this.levelController.chunkIDAtTime(this.media.currentTime) + 3),
							endOffset: Number.POSITIVE_INFINITY
						});
					}
					else {
						logger.log('skipped flush buffers: nothing to flush');
					}
				}
				//Safari can't use the level-based flush, it tends to crash the media element if it encounters a buffer hole
				else if(levelFlush && !this.browSafari) {
					this.loadLevel = -1; //force a level switch
					this.bufferController.flushLevels(0, Number.POSITIVE_INFINITY, this.levelController.chunkIDAtTime(this.media.currentTime));
				}
				else {
					this.trigger(Event.BUFFER_FLUSHING, {
						startOffset: this.levelController.endOfChunk(this.levelController.chunkIDAtTime(this.media.currentTime) + 1),
						endOffset: Number.POSITIVE_INFINITY
					});
					if(this.browFirefox) {
						this.trigger(Event.BUFFER_FLUSHING, {
							startOffset: 0,
							endOffset: this.levelController.endOfChunk(this.levelController.chunkIDAtTime(this.media.currentTime) - 1)
						});
					}
					else {
						this.trigger(Event.BUFFER_FLUSHING, {
							startOffset: Number.NEGATIVE_INFINITY,
							endOffset: this.levelController.endOfChunk(this.levelController.chunkIDAtTime(this.media.currentTime) - 1)
						});
					}
				}

				return true;
			}
			else {
				logger.log('cannot flush buffers: cannot find chunk timestamps');

				return false;
			}
		}
		else {
			logger.log('cannot flush buffers: media not attached');

			return false;
		}
	}
	//Mactrix additions end

	/** Return all quality levels **/
	get levels() {
		return this.levelController.levels;
	}

	/** Return current playback quality level **/
	get currentLevel() {
		return this.streamController.currentLevel;
	}

	/* set quality level immediately (-1 for automatic level selection) */
	set currentLevel(newLevel) {
		logger.log(`set currentLevel:${newLevel}`);
		this.loadLevel = newLevel;
		this.streamController.immediateLevelSwitch();
	}

	/** Return next playback quality level (quality level of next fragment) **/
	get nextLevel() {
		return this.streamController.nextLevel;
	}

	/* set quality level for next fragment (-1 for automatic level selection) */
	set nextLevel(newLevel) {
		logger.log(`set nextLevel:${newLevel}`);
		this.levelController.manualLevel = newLevel;
		this.streamController.nextLevelSwitch();
	}

	/** Return the quality level of current/last loaded fragment **/
	get loadLevel() {
		return this.levelController.level;
	}

	/* set quality level for current/next loaded fragment (-1 for automatic level selection) */
	set loadLevel(newLevel) {
		logger.log(`set loadLevel:${newLevel}`);
		this.levelController.manualLevel = newLevel;
	}

	/** Return the quality level of next loaded fragment **/
	get nextLoadLevel() {
		return this.levelController.nextLoadLevel;
	}

	/** set quality level of next loaded fragment **/
	set nextLoadLevel(level) {
		this.levelController.nextLoadLevel = level;
	}

	/** Return first level (index of first level referenced in manifest)
	 **/
	get firstLevel() {
		return Math.max(this.levelController.firstLevel, this.minAutoLevel);
	}

	/** set first level (index of first level referenced in manifest)
	 **/
	set firstLevel(newLevel) {
		logger.log(`set firstLevel:${newLevel}`);
		this.levelController.firstLevel = newLevel;
	}

	/** Return start level (level of first fragment that will be played back)
		 if not overrided by user, first level appearing in manifest will be used as start level
		if -1 : automatic start level selection, playback will start from level matching download bandwidth (determined from download of first segment)
	**/
	get startLevel() {
		return this.levelController.startLevel;
	}

	/** set  start level (level of first fragment that will be played back)
		 if not overrided by user, first level appearing in manifest will be used as start level
		if -1 : automatic start level selection, playback will start from level matching download bandwidth (determined from download of first segment)
	**/
	set startLevel(newLevel) {
		logger.log(`set startLevel:${newLevel}`);
		const hls = this;
		// if not in automatic start level detection, ensure startLevel is greater than minAutoLevel
		if (newLevel !== -1) {
		newLevel = Math.max(newLevel,hls.minAutoLevel);
		}
		hls.levelController.startLevel = newLevel;
	}

	/** Return the capping/max level value that could be used by automatic level selection algorithm **/
	get autoLevelCapping() {
		return this._autoLevelCapping;
	}

	/** set the capping/max level value that could be used by automatic level selection algorithm **/
	set autoLevelCapping(newLevel) {
		logger.log(`set autoLevelCapping:${newLevel}`);
		this._autoLevelCapping = newLevel;
	}

	//Mactrix additions start
	//Expose the minimum level constraint for use, as directly altering the config doesn't work any more
	get minAutoBitrate() {
		return this._minAutoBitrate;
	}

	set minAutoBitrate(newBitrate) {
		logger.log(`set minAutoBitrate:${newBitrate}`);
		this._minAutoBitrate = newBitrate;
	}
	//Mactrix additions end

	/* check if we are in automatic level selection mode */
	get autoLevelEnabled() {
		return (this.levelController.manualLevel === -1);
	}

	/* return manual level */
	get manualLevel() {
		return this.levelController.manualLevel;
	}

	/* return min level selectable in auto mode according to config.minAutoBitrate */
	get minAutoLevel() {
		/*
		let hls = this, levels = hls.levels, minAutoBitrate = hls.config.minAutoBitrate, len = levels ? levels.length : 0;
		*/
		let hls = this, levels = hls.levels, minAutoBitrate = hls.minAutoBitrate, len = levels ? levels.length : 0;
		for (let i = 0; i < len; i++) {
		const levelNextBitrate = levels[i].realBitrate ? Math.max(levels[i].realBitrate,levels[i].bitrate) : levels[i].bitrate;
		if (levelNextBitrate > minAutoBitrate) {
			return i;
		}
		}
		return 0;
	}

	/* return max level selectable in auto mode according to autoLevelCapping */
	get maxAutoLevel() {
		const hls = this;
		const levels = hls.levels;
		const autoLevelCapping = hls.autoLevelCapping;
		let maxAutoLevel;
		if (autoLevelCapping=== -1 && levels && levels.length) {
		maxAutoLevel = levels.length - 1;
		} else {
		maxAutoLevel = autoLevelCapping;
		}
		return maxAutoLevel;
	}

	// return next auto level
	get nextAutoLevel() {
		const hls = this;
		// ensure next auto level is between  min and max auto level
		return Math.min(Math.max(hls.abrController.nextAutoLevel,hls.minAutoLevel),hls.maxAutoLevel);
	}

	// this setter is used to force next auto level
	// this is useful to force a switch down in auto mode : in case of load error on level N, hls.js can set nextAutoLevel to N-1 for example)
	// forced value is valid for one fragment. upon succesful frag loading at forced level, this value will be resetted to -1 by ABR controller
	set nextAutoLevel(nextLevel) {
		const hls = this;
		hls.abrController.nextAutoLevel = Math.max(hls.minAutoLevel,nextLevel);
	}

	/** get alternate audio tracks list from playlist **/
	get audioTracks() {
		const audioTrackController = this.audioTrackController;
		return audioTrackController ? audioTrackController.audioTracks : [];
	}

	/** get index of the selected audio track (index in audio track lists) **/
	get audioTrack() {
		const audioTrackController = this.audioTrackController;
		return audioTrackController ? audioTrackController.audioTrack : -1;
	}

	/** select an audio track, based on its index in audio track lists**/
	set audioTrack(audioTrackId) {
		const audioTrackController = this.audioTrackController;
		if (audioTrackController) {
		audioTrackController.audioTrack = audioTrackId;
		}
		//Mactrix additions start
		//guarantee a buffer flush on audio track change
		if (this.bufferController.sourceBuffer['audio']) {
			if(this.browSafari) {
				this.trigger(Event.BUFFER_FLUSHING, {startOffset: 0, endOffset: Number.POSITIVE_INFINITY });
			}
			else {
				this.trigger(Event.BUFFER_FLUSHING, {startOffset: 0, endOffset: Number.POSITIVE_INFINITY, type: 'audio' });
			}
		}
		//Mactrix additions end
	}

	get liveSyncPosition() {
		return this.streamController.liveSyncPosition;
	}

	/** get alternate subtitle tracks list from playlist **/
	get subtitleTracks() {
		const subtitleTrackController = this.subtitleTrackController;
		return subtitleTrackController ? subtitleTrackController.subtitleTracks : [];
	}

	/** get index of the selected subtitle track (index in subtitle track lists) **/
	get subtitleTrack() {
		const subtitleTrackController = this.subtitleTrackController;
		return subtitleTrackController ? subtitleTrackController.subtitleTrack : -1;
	}

	/** select an subtitle track, based on its index in subtitle track lists**/
	set subtitleTrack(subtitleTrackId) {
		const subtitleTrackController = this.subtitleTrackController;
		if (subtitleTrackController) {
			subtitleTrackController.subtitleTrack = subtitleTrackId;
		}
	}

//Mactrix additions start
	set subtitleIndex(value) {
		this.trigger(Event.TEXT_TRACK_CHANGED, {index: value});
	}

	startPositionPdt(value) {
		this.streamCoordinator.startPdt = value;
	}

	set resumeBasePdt(value) {
		this.streamCoordinator.resumeBasePdt = value;
	}

	set timeouts(value) {
		this.config.manifestLoadingTimeOut = value.manifestTimeout;
		this.config.manifestLoadingMaxRetryTimeout = value.manifestTimeoutMax;
		this.config.levelLoadingTimeOut = value.variantTimeout;
		this.config.levelLoadingMaxRetryTimeout = value.variantTimeoutMax;
		this.config.fragLoadingTimeOut = value.chunkTimeout;
		this.config.fragLoadingMaxRetryTimeout = value.chunkTimeoutMax;
		this.config.streamTimeOut = value.streamTimeout;
	}

	_doCompleteSlowDetach() {
		this.trigger(Event.MEDIA_DETACHING);
		this.media = null;
		this.resumeFetch();
		this._inSlowDetach = false;
	}

	getTimeForPdt(pdt) {
		return this.streamCoordinator.getTimeForPdt(pdt);
	}

	getPdtForTime(time) {
		return this.streamCoordinator.getPdtForTime(time);
	}

	set bufferBeforeSeeking(seconds) {
		if (this.streamController.bufferBeforeSeeking === undefined) {
			this.timelineController.preFlushed = true;
			this.trigger(Event.BUFFER_FLUSHING, {startOffset: 0, endOffset: Number.POSITIVE_INFINITY });
			if(!this.browFirefox) {
				this.trigger(Event.BUFFER_FLUSHING, {startOffset: Number.NEGATIVE_INFINITY, endOffset: 0 });
			}
		}

		this.streamController.bufferBeforeSeeking = seconds;
		if (this.streamCoordinator.checkIfRenditionsAreActive) {
			this.audioStreamController.bufferBeforeSeeking = seconds;
		}
	}

	getCdmListAsync() {
		return this.licenseManager.drmCapabilities;
	}

	//Mactrix additions end
}

export default Hls;



// WEBPACK FOOTER //
// ./src/hlsjs/hls.js